import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextInput, Button } from '../../../../../ds';
import {
  validateNumerical,
  validateDecimal,
} from '../../../../../Utils/formValidations';
import { autoCompleteDecimalNumber } from '../../../../../Utils/formats';
import { orderProductsMaxLimit } from '../../../../../Utils/constants';
import { getUser } from '../../../../../mockData';
import MerchantPaymentsService from '../../../../../services/MerchantPaymentsService';
import { useStore } from '../../../../../services/store';
import styles from './CreateOrderStep.module.scss';

const CreateOrderStep = ({ formData: persistedData, onNext, onClose }) => {
  const [formData, setFormData] = useState({
    quantity: persistedData?.quantity || '',
    amount: persistedData?.amount || '',
    reference: persistedData?.reference || '',
  });

  const [validations, setValidations] = useState({
    quantity: '',
    amount: '',
  });

  const [wasOnFocus, setWasOnFocus] = useState({
    quantity: false,
    amount: false,
    reference: false,
  });

  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const user = getUser();
  const {
    qrCodeStore: { storeId },
  } = useStore();

  useEffect(() => {
    const isQuantityValid =
      formData.quantity &&
      parseInt(formData.quantity) >= 1 &&
      parseInt(formData.quantity) <= orderProductsMaxLimit;
    const isAmountValid = formData.amount && parseFloat(formData.amount) > 59;
    const hasValidStoreId = Boolean(storeId);
    setIsDisabled(!isQuantityValid || !isAmountValid || !hasValidStoreId);
  }, [formData.quantity, formData.amount, storeId]);

  useEffect(() => {
    setApiError('');
    setValidations({
      quantity: '',
      amount: '',
    });
  }, [storeId]);

  useEffect(() => {
    if (
      persistedData &&
      (persistedData.quantity ||
        persistedData.amount ||
        persistedData.reference)
    ) {
      setWasOnFocus({
        quantity: !!persistedData.quantity,
        amount: !!persistedData.amount,
        reference: !!persistedData.reference,
      });
    }
  }, [persistedData]);

  const handleInputChange = (field, value) => {
    if (field === 'quantity') {
      if (value === '' || validateNumerical(value)) {
        setFormData(prev => ({ ...prev, quantity: value }));
      }
    } else if (field === 'amount') {
      if (validateDecimal(value)) {
        setFormData(prev => ({
          ...prev,
          amount: value === '0' ? '' : value,
        }));
      }
    } else if (field === 'reference') {
      setFormData(prev => ({ ...prev, reference: value }));
    }
  };

  const handleInputBlur = (field, value) => {
    if (field === 'quantity') {
      quantityInputOnBlur(value);
    } else if (field === 'amount') {
      amountInputOnBlur(value);
    } else if (field === 'reference') {
      if (!wasOnFocus.reference) {
        setWasOnFocus(prev => ({ ...prev, reference: true }));
      }
    }
  };

  const quantityInputOnBlur = value => {
    if (!wasOnFocus.quantity) {
      setWasOnFocus(prev => ({ ...prev, quantity: true }));
    }

    const processedValue = value !== '' ? parseInt(value) : '';

    if (processedValue && processedValue < 1) {
      setValidations(prev => ({
        ...prev,
        quantity: 'La cantidad mínima es 1',
      }));
    } else if (processedValue && processedValue > orderProductsMaxLimit) {
      setValidations(prev => ({
        ...prev,
        quantity: `La cantidad máxima es ${orderProductsMaxLimit}`,
      }));
    } else {
      setValidations(prev => ({
        ...prev,
        quantity: '',
      }));
    }
  };

  const amountInputOnBlur = value => {
    if (!wasOnFocus.amount) {
      setWasOnFocus(prev => ({ ...prev, amount: true }));
    }

    const autoCompleteValue = autoCompleteDecimalNumber(value, 2);
    setFormData(prev => ({
      ...prev,
      amount: autoCompleteValue,
    }));

    if (autoCompleteValue && parseFloat(autoCompleteValue) <= 59) {
      setValidations(prev => ({
        ...prev,
        amount: 'El monto mínimo es $60.00',
      }));
    } else {
      setValidations(prev => ({
        ...prev,
        amount: '',
      }));
    }
  };

  const handleContinue = async () => {
    if (!storeId) {
      setApiError(
        'No se ha seleccionado una tienda válida para crear la orden.'
      );
      return;
    }

    setIsLoading(true);
    setApiError('');

    const order = {
      store_id: storeId,
      order_reference: formData.reference || '',
      items_count: parseInt(formData.quantity, 10),
      shipping_amount: 0,
      total: Number(formData.amount),
      order_assistant: '',
    };

    const requestData = {
      staff_id: user.employeeId().toString(),
      order: order,
    };

    try {
      const response = await MerchantPaymentsService().createOrder(requestData);

      setIsLoading(false);

      onNext({
        ...formData,
        orderId: response.data.order_id,
        orderData: order,
        serviceResponse: response,
      });
    } catch (error) {
      setIsLoading(false);
      const errorCode = error?.response?.data?.code;

      switch (errorCode) {
        case 'invalid-payment-data':
        case 'update-payment-failed':
        case 'updatePaymentFailed':
        case 'AmountNotInRange':
          setValidations(prev => ({
            ...prev,
            amount: 'El monto está fuera del rango permitido por la tienda.',
          }));
          break;
        case 'OrderReferenceUsed':
        case 'ParamInvalid':
          setValidations(prev => ({
            ...prev,
            reference: 'Ingresa una nueva referencia de compra.',
          }));
          break;
        default:
          setApiError('Error al crear la orden. Por favor, intenta de nuevo.');
      }
    }
  };

  return (
    <div className={styles.createOrderContainer}>
      <div className={styles.createOrderHeader}>
        <span onClick={onClose} className={styles.createOrderCloseBtn}>
          ×
        </span>
      </div>

      <h3 className={styles.createOrderTitle}>Crear orden</h3>

      <div className={styles.createOrderForm}>
        <p className={styles.createOrderDescription}>
          Llena los campos para crear la orden
        </p>

        <TextInput
          className={`k-ds-m-bottom-sp-06 ${styles.createOrderInput}`}
          errorMessage={wasOnFocus.quantity ? validations.quantity : ''}
          label='Número de artículos'
          onChange={e => handleInputChange('quantity', e.target.value)}
          onBlur={e => handleInputBlur('quantity', e.target.value)}
          placeholder='Ingresa el número de artículos'
          type='outline'
          required
          value={formData.quantity}
        />

        <TextInput
          className={`k-ds-m-bottom-sp-06 ${styles.createOrderInput}`}
          errorMessage={wasOnFocus.amount ? validations.amount : ''}
          label='Monto total'
          onChange={e => handleInputChange('amount', e.target.value)}
          onBlur={e => handleInputBlur('amount', e.target.value)}
          placeholder='$0.00'
          type='outline'
          required
          value={formData.amount}
        />

        <TextInput
          className={`k-ds-m-bottom-sp-06 ${styles.createOrderInput}`}
          label={
            <span className={styles.referenceLabel}>
              Referencia <span className={styles.optionalText}>(opcional)</span>
            </span>
          }
          onChange={e => handleInputChange('reference', e.target.value)}
          onBlur={e => handleInputBlur('reference', e.target.value)}
          placeholder='Referencia de la orden'
          type='outline'
          value={formData.reference}
        />
      </div>

      {apiError && <div className={styles.createOrderError}>{apiError}</div>}

      <Button
        type='primary-alternate'
        className={`k-ds-m-rigth-sp-02 ${styles.createOrderButton}`}
        horizontalPadding='22px'
        verticalPadding='16px'
        size='small'
        onClick={handleContinue}
        disabled={isDisabled || isLoading}
      >
        <span className={styles.createOrderButtonText}>
          {isLoading ? 'Creando orden...' : 'Continuar'}
        </span>
      </Button>
    </div>
  );
};

CreateOrderStep.propTypes = {
  formData: PropTypes.object,
  onNext: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CreateOrderStep;
